/**
 * Backend Service
 *
 * Factory and React context for backend abstraction layer.
 * Enables switching between Tauri (desktop) and WebRTC (web) backends.
 */

import { createContext, useContext } from 'react';
import type { IBackend, BackendType } from './IBackend';
import { WebRTCBackend } from './WebRTCBackend';
import { CefBackend, isCefHost } from './CefBackend';
import { peerConnection } from '../peerConnection';
import { logger } from '@/lib/logger';

// Re-export types and interfaces
export type { IBackend, BackendType } from './IBackend';
export type {
  IProjectBackend,
  IFileBackend,
  ITranscriptionBackend,
  ISystemBackend,
  ITerminalBackend,
} from './IBackend';
export * from './types';
export { WebRTCBackend, createWebRTCBackend } from './WebRTCBackend';
export { CefBackend, createCefBackend, isCefHost } from './CefBackend';

/**
 * Mock backend for development/testing without Tauri
 */
class MockBackend implements IBackend {
  readonly type: BackendType = 'mock';
  readonly isReady: boolean = true;

  projects = {
    create: async () => {
      throw new Error('Mock backend: projects.create not implemented');
    },
    listZips: async () => [],
    createFromZip: async () => {
      throw new Error('Mock backend: projects.createFromZip not implemented');
    },
    extractZip: async () => {
      throw new Error('Mock backend: projects.extractZip not implemented');
    },
    list: async () => [],
    get: async () => null,
    update: async () => {
      throw new Error('Mock backend: projects.update not implemented');
    },
    delete: async () => false,
    hasRecentActivity: async () => false,
    analyze: async () => ({
      suggested_name: 'Mock Project',
      suggested_description: '',
      detected_languages: [],
      detected_frameworks: [],
      file_count: 0,
      has_git: false,
    }),
    analyzeWithAI: async () => ({
      suggested_name: 'Mock Project',
      suggested_description: '',
      detected_languages: [],
      detected_frameworks: [],
      file_count: 0,
      has_git: false,
    }),
    generateDetails: async () => ({
      name: 'Mock Project',
      description: 'Mock project description',
    }),
    generateRunConfigs: async () => [],
    isAIServiceAvailable: async () => false,
    getStats: async () => ({ commits: 0, messages: 0 }),
  } as IBackend['projects'];

  artifacts = {
    list: async () => [],
  } as IBackend['artifacts'];

  files = {
    pickFolder: async () => null,
    pickFile: async () => null,
    readFile: async () => '',
    getFolderChildren: async (_projectId?: string, _folderPath?: string, _parentGitignored?: boolean) => [],
    readProjectFiles: async () => [],
    crawlProjectTree: async () => [],
    indexProjectFiles: async () => [],
    getGitStatus: async () => [],
    writeFile: async () => { throw new Error('Mock: writeFile not implemented'); },
    readFileBase64: async () => { throw new Error('Mock: readFileBase64 not implemented'); },
    saveAttachment: async () => { throw new Error('Mock: saveAttachment not implemented'); },
    revealInExplorer: async () => { throw new Error('Mock: revealInExplorer not implemented'); },
    openExternal: async () => { throw new Error('Mock: openExternal not implemented'); },
    renameFile: async () => { throw new Error('Mock: renameFile not implemented'); },
    deleteFile: async () => { throw new Error('Mock: deleteFile not implemented'); },
    createFile: async () => { throw new Error('Mock: createFile not implemented'); },
    createFolder: async () => { throw new Error('Mock: createFolder not implemented'); },
    pathExistsInProject: async () => false,
    copyExternalIntoProject: async () => { throw new Error('Mock: copyExternalIntoProject not implemented'); },
    moveWithinProject: async () => { throw new Error('Mock: moveWithinProject not implemented'); },
    initAteliercodeConfig: async () => {},
    readAteliercodeRunners: async () => null,
    writeAteliercodeRunners: async () => {},
    readAteliercodeConfig: async () => null,
    writeAteliercodeConfig: async () => {},
    cacheRead: async () => null,
    cacheWrite: async () => {},
    cacheClear: async () => {},
    cacheClearAll: async () => 0,
  } as IBackend['files'];

  transcription = {
    transcribeOpenAI: async () => ({ text: '' }),
    getWhisperSettings: async () => ({
      provider: 'none' as const,
      openaiApiKey: '',
      transcriptionMode: 'live' as const,
      voiceEngineUrl: 'https://transcribe.unveiledsoftwaresolutions.com',
      voiceEngineToken: '',
      voiceEngineModel: 'medium' as const,
    }),
    enhanceTranscription: async () => ({ text: '' }),
  } as IBackend['transcription'];

  system = {
    detectAgents: async () => [],
    getHostname: async () => 'mock-host',
    getPlatform: async () => 'mock',
    listAgentModels: async () => [],
    readLogs: async () => [],
  } as IBackend['system'];

  terminal = {
    list: async () => [],
    listSessions: async () => [],
    create: async () => 0,
    write: async () => {},
    resize: async () => {},
    close: async () => {},
    detach: async () => {},
    attach: async () => '',
    resume: async () => {},
    getBuffer: async () => '',
    markAgentSession: async () => {},
    onData: () => () => {},
    onExit: () => () => {},
    onActivity: () => () => {},
    onPatternMatch: () => () => {},
    persistAll: async () => {},
  } as IBackend['terminal'];

  pipeline = {
    getConfig: async () => ({ ticketsDir: 'tickets', stages: [], readyChecklist: [] }),
    setConfig: async () => {},
    getPromptTemplate: async () => '',
    setPromptTemplate: async () => {},
    listTickets: async () => [],
    readTicket: async () => { throw new Error('Mock: readTicket not implemented'); },
    writeTicket: async () => { throw new Error('Mock: writeTicket not implemented'); },
    createTicket: async () => { throw new Error('Mock: createTicket not implemented'); },
    deleteTicket: async () => {},
    setTicketState: async () => { throw new Error('Mock: setTicketState not implemented'); },
    advanceTicket: async () => { throw new Error('Mock: advanceTicket not implemented'); },
    recordTranscript: async () => { throw new Error('Mock: recordTranscript not implemented'); },
    listTranscripts: async () => [],
    readTranscript: async () => '',
    prepareStageRun: async () => { throw new Error('Mock: prepareStageRun not implemented'); },
    prepareTriageRun: async () => { throw new Error('Mock: prepareTriageRun not implemented'); },
    getAgentProfiles: async () => ({ profiles: [] }),
    setAgentProfiles: async () => {},
    recordRunStart: async () => 'mock-run',
    recordRunEnd: async () => {},
    listRuns: async () => [],
    listActiveRuns: async () => [],
    ensureTicketWorktree: async () => { throw new Error('Mock: ensureTicketWorktree not implemented'); },
    ticketDiff: async () => { throw new Error('Mock: ticketDiff not implemented'); },
    openTicketPr: async () => { throw new Error('Mock: openTicketPr not implemented'); },
    removeTicketWorktree: async () => {},
    listRunners: async () => [],
    generateQaRunners: async () => { throw new Error('Mock: generateQaRunners not implemented'); },
    runRunner: async () => { throw new Error('Mock: runRunner not implemented'); },
    readRunLog: async () => '',
    exportPipelineTemplate: async () => { throw new Error('Mock: exportPipelineTemplate not implemented'); },
    importPipelineTemplate: async () => { throw new Error('Mock: importPipelineTemplate not implemented'); },
  } as IBackend['pipeline'];

  queue = {
    list: async () => [],
    readTicket: async () => '',
    readLog: async () => '',
    moveTicket: async () => {},
    listEpics: async () => [],
    setEngine: async () => {},
    submitFeatureRequest: async () => { throw new Error('Mock: queue.submitFeatureRequest not implemented'); },
    inboxList: async () => [],
    inboxCreate: async () => { throw new Error('Mock: queue.inboxCreate not implemented'); },
    inboxUpdate: async () => {},
    inboxDelete: async () => {},
    inboxLinkTicket: async () => {},
    inboxArchive: async () => {},
    invokeScript: async () => '',
    startRun: async () => { throw new Error('Mock: queue.startRun not implemented'); },
    stopRun: async () => {},
    listRuns: async () => [],
    onRunOutput: () => () => {},
    onRunExit: () => () => {},
  } as IBackend['queue'];

  identity = {
    list: async () => [],
    upsert: async () => { throw new Error('Mock: identity.upsert not implemented'); },
    remove: async () => { throw new Error('Mock: identity.remove not implemented'); },
    setDefault: async () => { throw new Error('Mock: identity.setDefault not implemented'); },
    getDefault: async () => null,
    status: async () => ({ authenticated: false, expiresAt: null, subscriptionType: null }),
  } as IBackend['identity'];

  copilotIdentity = {
    list: async () => [],
    upsert: async () => { throw new Error('Mock: copilotIdentity.upsert not implemented'); },
    remove: async () => { throw new Error('Mock: copilotIdentity.remove not implemented'); },
    setDefault: async () => { throw new Error('Mock: copilotIdentity.setDefault not implemented'); },
    getDefault: async () => null,
    status: async () => ({ initialized: false }),
  } as IBackend['copilotIdentity'];

  groups = {
    list: async () => [],
    upsert: async () => { throw new Error('Mock: groups.upsert not implemented'); },
    remove: async () => { throw new Error('Mock: groups.remove not implemented'); },
    reorder: async () => { throw new Error('Mock: groups.reorder not implemented'); },
  } as IBackend['groups'];
}

// Singleton backend instance
let backendInstance: IBackend | null = null;

/**
 * Initialize the backend based on the environment
 */
export function initializeBackend(): IBackend {
  if (backendInstance) {
    return backendInstance;
  }

  // CEF host shell (local-desktop replacement for Tauri). Detected via the
  // `window.cefQuery` bridge. Takes priority over Tauri/web so the migrated app
  // runs against the headless core over IPC. The WS connection / endpoint
  // discovery is kicked off eagerly here (mirrors how other backends become
  // usable immediately); individual requests also connect lazily, so a slow
  // connect never blocks `getBackend()`.
  if (isCefHost()) {
    logger.debug('Backend', 'Initializing CefBackend (CEF host shell)');
    const cef = new CefBackend();
    cef.connect().catch((err) =>
      logger.warn('Backend', `CefBackend eager connect failed (will retry lazily): ${String(err)}`),
    );
    backendInstance = cef;
    return backendInstance;
  }

  if (peerConnection.isClient) {
    logger.debug('Backend', 'Initializing WebRTCBackend (connected as client)');
    backendInstance = new WebRTCBackend();
    return backendInstance;
  }

  logger.debug('Backend', 'Initializing MockBackend (no Tauri available)');
  backendInstance = new MockBackend();
  return backendInstance;
}

/**
 * Switch to WebRTC backend when connected to a remote machine as a CLIENT
 */
export function switchToWebRTCBackend(): IBackend {
  if (!peerConnection.isConnected) {
    throw new Error('Cannot switch to WebRTC backend: not connected');
  }
  if (peerConnection.isHost) {
    logger.warn('Backend', 'Ignoring switchToWebRTCBackend: this machine is the host, not the client');
    return getBackend();
  }
  logger.debug('Backend', 'Switching to WebRTCBackend');
  backendInstance = new WebRTCBackend();
  return backendInstance;
}

/**
 * Switch back to the local CEF backend (e.g. after disconnecting from a remote machine)
 */
export function switchToCefBackend(): IBackend {
  logger.debug('Backend', 'Switching to local CefBackend');
  const cef = new CefBackend();
  cef.connect().catch((err) => logger.warn('Backend', `CefBackend connect failed: ${String(err)}`));
  backendInstance = cef;
  return backendInstance;
}

/**
 * Get the current backend instance
 */
export function getBackend(): IBackend {
  if (!backendInstance) {
    return initializeBackend();
  }
  return backendInstance;
}

/**
 * Set a custom backend instance (useful for testing or WebRTC)
 */
export function setBackend(backend: IBackend): void {
  backendInstance = backend;
}

/**
 * React context for the backend
 */
export const BackendContext = createContext<IBackend | null>(null);

/**
 * Hook to access the backend from React components
 */
export function useBackend(): IBackend {
  const context = useContext(BackendContext);
  if (context) {
    return context;
  }
  return getBackend();
}

/**
 * Check which backend type is active
 */
export function getBackendType(): BackendType {
  return getBackend().type;
}
